import { FieldValues } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import {
  deleteFertilizantes,
  deleteCombustibles,
  getFertilizantes,
  getCombustibles,
  createProduct,
  CreateProductProps,
  getProductDetail,
  editProduct,
  EditProductProps,
} from "@/services/products.service";
import { toaster } from "@/components/ui/toaster";
import { handlePermissionError } from "@/utils/permission-check";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { AppRoutes } from "@/constants/routes";
import { ProductEntity } from "./products.schema";
import { TipoInsumos } from "@/constants/enums";

interface FetchProductsSectionHandlerProps {
  setLoadingQuery: (loading: boolean) => void;
  setFertilizantes: (data: ProductEntity[] | undefined) => void;
  setCombustibles: (data: ProductEntity[] | undefined) => void;
  filters?: any;
  section: string;
  showLoad: boolean;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchProductsSectionHandler = async ({
  setLoadingQuery,
  setFertilizantes,
  setCombustibles,
  filters,
  section,
  showLoad,
  navigate,
  fetchPermissions,
}: FetchProductsSectionHandlerProps) => {
  try {
    if (showLoad) setLoadingQuery(true);

    let data;
    
    switch (section) {
      case "Fertilizantes":
        data = await getFertilizantes(filters);
        setFertilizantes(data);
        break;
      case "Combustibles":
        data = await getCombustibles(filters);
        setCombustibles(data);
        break;
      default:
        break;
    }
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron obtener los productos",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
  } finally {
    if (showLoad) setLoadingQuery(false);
  }
};

interface ClearFiltersHandlerProps {
  getValues: () => any;
  setFiltersApplied: (applied: boolean) => void;
  setValue: (name: string, value: any) => void;
  submitFilters: (data: any) => Promise<void>;
  setCurrentFilters: (filters: any) => void;
}

export const clearFiltersHandler = async ({
  getValues,
  setFiltersApplied,
  setValue,
  submitFilters,
  setCurrentFilters,
}: ClearFiltersHandlerProps) => {
  const currentValues = getValues();
  const clearedValues = Object.keys(currentValues).reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {} as any);

  Object.keys(clearedValues).forEach((key) => {
    setValue(key, "");
  });

  setFiltersApplied(false);
  setCurrentFilters({});
  await submitFilters(clearedValues);
};

interface SubmitFiltersProductsSectionProps {
  event: FieldValues;
  fetchSection: (section: string, filters?: any) => Promise<void>;
  setFiltersApplied: (applied: boolean) => void;
  setOpenFilters: (open: boolean) => void;
  currentSection: string;
  setCurrentFilters: (filters: any) => void;
}

export const submitFiltersProductsSection = async ({
  event,
  fetchSection,
  setFiltersApplied,
  setOpenFilters,
  currentSection,
  setCurrentFilters,
}: SubmitFiltersProductsSectionProps) => {
  // Mapeo de campos del frontend al backend
  const fieldMapping: Record<string, string> = {
    name: "nombre",
    descripcion: "descripcion", 
    activo: "activo"
  };
  
  // Filtrar campos vacíos y mapear nombres para el backend
  const cleanedFilters = Object.entries(event).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      const backendFieldName = fieldMapping[key] || key;
      acc[backendFieldName] = value;
    }
    return acc;
  }, {} as any);
  
  const hasFilters = Object.keys(cleanedFilters).length > 0;
  
  setFiltersApplied(hasFilters);
  setOpenFilters(false);
  setCurrentFilters(event); // Guardamos el estado completo para la UI
  
  // Enviamos solo los filtros con valores al backend con nombres correctos
  await fetchSection(currentSection, hasFilters ? cleanedFilters : undefined);
};

interface HandleDeleteProductsHandlerProps {
  section: string;
  selection: string[];
  setLoadingDeleteProducts: (loading: boolean) => void;
  setOpenConfirm: (open: boolean) => void;
  fetchSection: (section: string, filters?: any, showLoad?: boolean) => Promise<void>;
  setSelection: (selection: string[]) => void;
}

export const handleDeleteProductsHandler = async ({
  section,
  selection,
  setLoadingDeleteProducts,
  setOpenConfirm,
  fetchSection,
  setSelection,
}: HandleDeleteProductsHandlerProps) => {
  try {
    setLoadingDeleteProducts(true);
    
    const idsToDelete = selection.map(id => Number.parseInt(id));
    
    switch (section) {
      case "Fertilizantes":
        await deleteFertilizantes({ productos: idsToDelete });
        break;
      case "Combustibles":
        await deleteCombustibles({ productos: idsToDelete });
        break;
      default:
        break;
    }
    
    toaster.create({
      title: "Productos eliminados con éxito",
      type: "success",
    })
    
    setSelection([]);
    setOpenConfirm(false);
    fetchSection(section, {}, false);
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron eliminar los productos",
    );
    
    toaster.create({
      type: "error",
      title: errorMessage,
    });
  } finally {
    setLoadingDeleteProducts(false);
  }
};

interface HandleCreateProductHandlerProps {
  getValues: () => any;
  setLoadingCreateProduct: (loading: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  tipoProducto: number
}

export const handleCreateProductHandler = async ({
  getValues,
  setLoadingCreateProduct,
  navigate,
  fetchPermissions,
  tipoProducto
}: HandleCreateProductHandlerProps) => {
  try {
    setLoadingCreateProduct(true);

    const values = getValues();
    
    const objToSend: CreateProductProps = {
      nombre: values.nombre,
      descripcion: values.descripcion,
      numAdmis: values.numAdmis || undefined,
      tipoProducto: tipoProducto
    };

    await createProduct(objToSend);

    toaster.create({
      type: "success",
      title: "Producto creado con éxito.",
    });
    
    // Navegar a la pestaña correcta según el tipo de producto
    const tabName = tipoProducto === TipoInsumos.Fertilizante ? "Fertilizantes" : "Combustibles";
    navigate(AppRoutes.PRODUCTS, { state: { tab: tabName } });
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo crear el producto",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingCreateProduct(false);
  }
};

interface FetchProductByIdHandlerProps {
  id: string;
  setLoadingProduct: (loading: boolean) => void;
  reset: (data: any) => void;
  setSelectedTipoInsumo: (tipo: string) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchProductByIdHandler = async ({
  id,
  setLoadingProduct,
  reset,
  setSelectedTipoInsumo,
  navigate,
  fetchPermissions,
}: FetchProductByIdHandlerProps) => {
  try {
    setLoadingProduct(true);

    const productData = await getProductDetail({ id: Number(id) });

    if (productData) {
      // Resetear el formulario con los datos del producto
      reset({
        nombre: productData.nombre,
        descripcion: productData.descripcion,
        numAdmis: productData.numAdmis || "",
        tipoProducto: productData.idTipoProducto,
        activo: [productData.activo],
      });

      // Establecer el tipo de insumo seleccionado
      setSelectedTipoInsumo(productData.idTipoProducto.toString());
    }
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo cargar el producto",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
  } finally {
    setLoadingProduct(false);
  }
};

interface HandleEditProductHandlerProps {
  id: string;
  getValues: () => any;
  setLoadingEditProduct: (loading: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  tipoProducto: number;
}

export const handleEditProductHandler = async ({
  id,
  getValues,
  setLoadingEditProduct,
  navigate,
  fetchPermissions,
  tipoProducto,
}: HandleEditProductHandlerProps) => {
  try {
    setLoadingEditProduct(true);

    const values = getValues();
    
    const objToSend: EditProductProps = {
      id: Number(id), 
      nombre: values.nombre,
      descripcion: values.descripcion,
      numAdmis: values.numAdmis || undefined,
      tipoProducto: tipoProducto,
      activo: values.activo,
    };

    await editProduct(objToSend);

    toaster.create({
      type: "success",
      title: "Producto actualizado con éxito.",
    });
    
    // Navegar a la pestaña correcta según el tipo de producto
    const tabName = tipoProducto === TipoInsumos.Fertilizante ? "Fertilizantes" : "Combustibles";
    navigate(AppRoutes.PRODUCTS, { state: { tab: tabName } });
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo actualizar el producto",
    );
    
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingEditProduct(false);
  }
};
